import { useCallback, useEffect, useState } from 'react'
import {
  CustomCell,
  CustomRenderer,
  drawTextCell,
  GridCellKind,
  ProvideEditorCallback,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { ITextCell } from '@/core/models/columns/text'
import { debounce } from '@/shared/utils'

const INCLUDED_TEXT_TYPES = [
  ColumnTypes.Text,
  ColumnTypes.Phone,
  ColumnTypes.Email,
  ColumnTypes.Slug,
  ColumnTypes.RowID,
  ColumnTypes.Address,
]

export interface CustomTextCellProps {
  readonly type: ColumnTypes
  readonly cell: ITextCell
}

export type CustomTextCell = CustomCell<CustomTextCellProps>

interface TextEditorProps {
  value?: string
  onChange: (nextValue: string) => void
}

// TODO
const TextEditor = ({ value, onChange }: TextEditorProps) => {
  const [textValue, setTextValue] = useState(value || '')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((newValue: string) => {
      if (newValue !== value) {
        onChange(newValue)
      }
    }, 300),
    [onChange, value]
  )

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setTextValue(newValue)
      debouncedOnChange(newValue)
    },
    [debouncedOnChange]
  )

  return <input type='text' value={textValue} onChange={handleChange} className='w-full h-full' autoFocus />
}

const Editor: ReturnType<ProvideEditorCallback<CustomTextCell>> = ({ value, onChange }) => {
  let cell = value.data.cell
  let cellValue = cell.toString()

  return (
    <TextEditor
      value={cellValue}
      onChange={(textValue) => {
        const updatedCell = cell.toCell(textValue) as ITextCell

        onChange({
          ...value,
          copyData: textValue,
          data: {
            type: value.data.type,
            cell: updatedCell,
          },
        })
      }}
    />
  )
}

const CustomTextRenderer: CustomRenderer<CustomTextCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomTextCell =>
    INCLUDED_TEXT_TYPES.includes((cell.data as CustomTextCellProps).type),
  draw: (args, cell) => {
    let value = cell.data.cell.toString()
    drawTextCell(args, value, cell.contentAlign)
  },
  provideEditor: () => ({
    editor: Editor,
  }),
}

export default CustomTextRenderer
